/**
 * 組織タイプ分類 (gb-org-questionnaire-74 / 組織レポート)
 *
 * 会社メンバーの gb74 個人スコアの **集約 A/B/C/D 値** から、team_judgment_patterns の
 * 10タイプ (RC/RD/LA/LB/BB/BC/TA/TD/CC/EE) のいずれかを判定する。
 *
 * 【方針】旧 `scoreConditions.TP` ("RC" 等の直接指定) は破棄し、集約 A/B/C/D で判定する
 *         (ユーザー確定: 判定方式を A/B/C/D 集約値に統一)。
 *
 * ダイヤモンド幾何 (Herrmann型ホールブレイン):
 *   A(論理/システム)=左上, B(秩序/安定)=左下, C(共感/人)=右下, D(創造/変化)=右上
 *   左脳L=A,B / 右脳R=C,D / 上側T=A,D / 下側B(bottom)=B,C
 *
 * 判定 (仮ロジック。閾値は集約 0〜12 スケール):
 *   1. 集約4軸のスプレッド(max-min)が小さい → 中央寄り
 *        - 個人の平均スプレッドが大きい(尖った個人が多い) → EE(中央薄) / それ以外 → CC(中央厚)
 *   2. それ以外 → 最強軸 + 近接サイドで方向型を決定
 *        A: B≥D→LA(左) / D>B→TA(上)   B: A≥C→LB(左) / C>A→BB(下)
 *        C: D≥B→RC(右) / B>D→BC(下)   D: C≥A→RD(右) / A>C→TD(上)
 */
import { getAxisScores, type AxisKey, type Gb74Scores } from './gb74Report';

export type OrgTypeCode =
  | 'RC'
  | 'RD'
  | 'LA'
  | 'LB'
  | 'BB'
  | 'BC'
  | 'TA'
  | 'TD'
  | 'CC'
  | 'EE';

export interface OrgTypeResult {
  code: OrgTypeCode;
  /** タイプ名 (team_judgment_patterns.description 相当) */
  label: string;
  /** 集約4軸平均 (0〜12) */
  axis: Record<AxisKey, number>;
  /** 判定根拠 (デバッグ・注記用) */
  rationale: string;
}

const LABELS: Record<OrgTypeCode, string> = {
  RC: '右脳優勢：C厚め（共感リード）',
  RD: '右脳優勢：D厚め（創造リード）',
  LA: '左脳優勢：A厚め（論理リード）',
  LB: '左脳優勢：B厚め（秩序リード）',
  BB: '下側優勢：B厚め（運用・現場）',
  BC: '下側優勢：C厚め（協働・現場）',
  TA: '上側優勢：A厚め（構想・精度）',
  TD: '上側優勢：D厚め（構想・変革）',
  CC: '中央が厚い（バランス型多）',
  EE: '中央が薄い（尖り多い）',
};

/** 集約4軸のスプレッドがこれ未満なら「中央寄り」(CC/EE候補) とみなす */
export const BALANCED_SPREAD = 2.0;
/** 個人の平均スプレッドがこれ以上なら「尖った個人が多い」(EE) とみなす */
export const PEAKED_INDIVIDUAL_SPREAD = 6.0;

/**
 * メンバーの gb74 スコア配列から組織タイプを判定する。
 * @param members 各メンバーの gb74 scores
 */
export function classifyOrgType(members: Array<{ scores: Gb74Scores }>): OrgTypeResult {
  const keys: AxisKey[] = ['A', 'B', 'C', 'D'];
  const agg: Record<AxisKey, number> = { A: 0, B: 0, C: 0, D: 0 };
  const n = Math.max(1, members.length);
  const perMemberSpread: number[] = [];

  for (const m of members) {
    const ax = getAxisScores(m.scores);
    keys.forEach((k) => (agg[k] += ax[k] ?? 0));
    const vals = keys.map((k) => ax[k] ?? 0);
    perMemberSpread.push(Math.max(...vals) - Math.min(...vals));
  }
  keys.forEach((k) => (agg[k] = Math.round((agg[k] / n) * 10) / 10));

  const vals = keys.map((k) => agg[k]);
  const spread = Math.max(...vals) - Math.min(...vals);
  const avgIndivSpread = perMemberSpread.reduce((a, b) => a + b, 0) / n;

  // 1. 中央寄り (集約が平坦)
  if (spread < BALANCED_SPREAD) {
    const code: OrgTypeCode = avgIndivSpread >= PEAKED_INDIVIDUAL_SPREAD ? 'EE' : 'CC';
    return {
      code,
      label: LABELS[code],
      axis: agg,
      rationale: `集約スプレッド ${spread.toFixed(1)} < ${BALANCED_SPREAD}（平坦）／個人平均スプレッド ${avgIndivSpread.toFixed(1)}`,
    };
  }

  // 2. 方向型 (最強軸 + 近接サイド)
  const top = [...keys].sort((x, y) => agg[y] - agg[x])[0];
  let code: OrgTypeCode;
  switch (top) {
    case 'A':
      code = agg.B >= agg.D ? 'LA' : 'TA';
      break;
    case 'B':
      code = agg.A >= agg.C ? 'LB' : 'BB';
      break;
    case 'C':
      code = agg.D >= agg.B ? 'RC' : 'BC';
      break;
    default: // 'D'
      code = agg.C >= agg.A ? 'RD' : 'TD';
      break;
  }
  return {
    code,
    label: LABELS[code],
    axis: agg,
    rationale: `最強軸=${top}／集約 A${agg.A} B${agg.B} C${agg.C} D${agg.D}`,
  };
}
